import React from "react";

/**
 * Header strip for the dashboard: the property selector on the left, the property wide
 * totals on the right. The two used to be separate cards of equal weight, which read as
 * "one more tile" rather than as a summary of everything below it - so they share one
 * surface here, and the saved units get the largest type on the page.
 */

const ACCENT = "#322FC8";
const LABEL = "#A6AAB0";
const VALUE = "#363B3F";

const styles = {
  bar: {
    background: "#FFFFFF",
    borderRadius: "14px",
    padding: "18px 24px",
    marginTop: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "20px",
  },
  selector: {
    flex: "1 1 300px",
    maxWidth: "420px",
  },
  stats: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "28px",
  },
  label: {
    color: LABEL,
    fontFamily: "manrope",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    whiteSpace: "nowrap",
  },
  headlineValue: {
    color: VALUE,
    fontFamily: "manrope",
    fontSize: "28px",
    fontWeight: 700,
    lineHeight: "34px",
    whiteSpace: "nowrap",
  },
  headlineCaption: {
    color: LABEL,
    fontFamily: "manrope",
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: "18px",
    whiteSpace: "nowrap",
  },
  headline: {
    borderLeft: `3px solid ${ACCENT}`,
    paddingLeft: "16px",
  },
  value: {
    color: VALUE,
    fontFamily: "manrope",
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "28px",
    whiteSpace: "nowrap",
  },
  divider: {
    width: "1px",
    alignSelf: "stretch",
    minHeight: "44px",
    background: "#ECEEF1",
  },
};

const Stat = ({ label, value }) => (
  <div>
    <div style={styles.label}>{label}</div>
    <div style={styles.value}>{value}</div>
  </div>
);

function PropertySummary({ deviceCount, unitsSaved, unitsConsumed, children }) {
  const total = unitsConsumed + unitsSaved;
  const savingPercentage = total > 0 ? (unitsSaved / total) * 100 : 0;

  return (
    <div style={styles.bar}>
      <div style={styles.selector}>{children}</div>

      {deviceCount > 0 && (
        <div style={styles.stats}>
          <div style={styles.headline}>
            <div style={styles.label}>Total Units Saved</div>
            <div style={styles.headlineValue}>{unitsSaved.toFixed(2)} kWh</div>
            <div style={styles.headlineCaption}>
              across {deviceCount} {deviceCount === 1 ? "meter" : "meters"}
            </div>
          </div>

          <div style={styles.divider} />

          <Stat label="Units Consumed" value={`${unitsConsumed.toFixed(2)} kWh`} />

          <div style={styles.divider} />

          <Stat label="Overall Saving" value={`${savingPercentage.toFixed(2)} %`} />
        </div>
      )}
    </div>
  );
}

export default PropertySummary;
